import { useAuth } from "@clerk/clerk-react";
import { Box, Button, Tooltip } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  InboxSidebarOptionKey,
  OutreachContactsContext,
  ScheduledType,
} from "contexts/OutreachContacts";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "schemas/routes";

import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import CancelAllEmailsDialog from "./CancelAllEmailsDialog";

export default function CancelAllButton() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);

  const {
    scheduledTotals,
    setScheduledTotals,
    setContacts,
    setTotals,
    setCurrentContact,
  } = useContext(OutreachContactsContext);

  const totalOutreach = scheduledTotals[ScheduledType.outreach] ?? 0;
  const totalFollowup = scheduledTotals[ScheduledType.followUp] ?? 0;

  // No total outreach email exist. So if user cancel pending emails
  // it will only cancel follow-up emails.
  const onlyFollowups = totalOutreach === 0 && totalFollowup > 0;

  const [loading, setLoading] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [includesFollowup, setIncludesFollowup] = useState(false);

  const openCancel = () => {
    setOpenConfirmationDialog(true);
    trackEvent("Cancel All Pending Emails Button Clicked");
  };

  const handleCancelAllPendingEmails = async () => {
    try {
      setLoading(true);
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/emails/cancel-all`,
        "POST",
        {},
        { includesFollowup: includesFollowup || onlyFollowups },
      );
      setAlert("All of your emails were successfully cancelled", "success");

      let totalDeleted = 0;
      setCurrentContact(null);
      setScheduledTotals((prev) => {
        totalDeleted += prev[ScheduledType.outreach] || 0;
        prev[ScheduledType.outreach] = 0;
        if (includesFollowup || onlyFollowups) {
          totalDeleted += prev[ScheduledType.followUp] || 0;
          prev[ScheduledType.followUp] = 0;
        }
        return prev;
      });
      setTotals((prev) => ({
        ...prev,
        [InboxSidebarOptionKey.scheduled]: Math.max(
          (prev[InboxSidebarOptionKey.scheduled] || 0) - totalDeleted,
          0,
        ),
      }));
      setContacts([]);
      setOpenConfirmationDialog(false);
      navigate(`/${routes.inboxScheduled}`);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="This button will stop all pending emails from being sent.">
        <Button
          sx={{
            fontSize: 12,
            color: "error.main",
            borderColor: "error.main",
            textTransform: "none",
          }}
          onClick={openCancel}
          disabled={
            !Boolean(scheduledTotals[ScheduledType.outreach]) &&
            !Boolean(scheduledTotals[ScheduledType.followUp])
          }
        >
          Cancel All{" "}
          <Box
            component="i"
            className="fa-solid fa-octagon-exclamation"
            sx={{ ml: 1 }}
          />
        </Button>
      </Tooltip>

      <CancelAllEmailsDialog
        open={openConfirmationDialog}
        handleClose={() => setOpenConfirmationDialog(false)}
        handleConfirm={handleCancelAllPendingEmails}
        loading={loading}
        setIncludesFollowup={setIncludesFollowup}
        totalOutreach={totalOutreach}
        totalFollowup={totalFollowup}
        onlyFollowups={onlyFollowups}
      />
    </>
  );
}
